import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Shield, Lock, Eye, Database, 
  UserCheck, Globe, CreditCard, Trash2, Server 
} from "lucide-react";

// NEW: Full production-ready Privacy Policy aligned with NestJS & Cloudflare R2 backend architecture
const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    // MODIFIED: Added `font-legal` to enforce the same readable typography hierarchy as the FAQ page.
    <div className="min-h-screen bg-background font-legal">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/30">
        <div className="container mx-auto px-6 py-4">
          <button
            onClick={() => navigate("/#footer")}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-sans text-sm md:text-base cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3.5 rounded-2xl bg-primary/10 border border-primary/20 shadow-sm">
                <Shield className="w-8 h-8 md:w-10 md:h-10 text-primary" />
              </div>
              <h1 className="text-4xl md:text-6xl font-display font-bold tracking-tight text-foreground">
                Privacy Policy
              </h1>
            </div>
            {/* MODIFIED: Increased font size and line height for laptop screen readability */}
            <p className="text-muted-foreground text-lg md:text-xl max-w-2xl leading-relaxed">
              Your privacy is our operational priority. This policy provides complete transparency regarding how our architecture protects, encrypts, and systematically purges your safety data.
            </p>
            <p className="text-sm md:text-base text-muted-foreground/70 mt-6 font-sans font-medium">
              Effective Date: July 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 pb-28">
        <div className="container mx-auto px-6">
          {/* MODIFIED: Increased spacing between sections (space-y-16 to space-y-20) to separate complex legal topics cleanly on large monitors */}
          <div className="max-w-4xl mx-auto space-y-16 md:space-y-20">

            {/* Section 1: Information We Collect */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                {/* MODIFIED: Upgraded icon container padding and contrast */}
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <Database className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    1. Information We Collect
                  </h2>
                  {/* MODIFIED: Standardized text scale to text-base/text-[16.5px] with leading-8 for effortless desktop reading */}
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>We collect only the essential data required to operate our timer-based safety verification system:</p>
                    <ul className="list-disc list-outside space-y-2.5 ml-6 marker:text-primary/70">
                      <li><strong className="text-foreground font-semibold">Account Data:</strong> Email address, basic profile information, and authentication credentials. Our service is intended strictly for individuals aged 18 and older.</li>
                      <li><strong className="text-foreground font-semibold">Trip Metadata:</strong> User-defined destination labels, expected arrival deadlines, and temporary geographic coordinates submitted at trip creation.</li>
                      <li><strong className="text-foreground font-semibold">Trusted Contacts:</strong> Email addresses and phone numbers of the emergency contacts you designate. You act as the Data Controller for these third-party contacts and must ensure you have their consent prior to addition.</li>
                      <li><strong className="text-foreground font-semibold">Heir Data (Dead Switch):</strong> Securely encrypted contact details for your designated Trustee, along with optional emergency media files uploaded by Pro tier users.</li>
                      <li><strong className="text-foreground font-semibold">Technical Telemetry:</strong> IP addresses and basic device interaction logs required for rate-limiting, DDoS protection, and preventing unauthorized system abuse.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Payments & Merchant of Record */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <CreditCard className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    2. Payments & Subscriptions
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>
                      Our order process is conducted by our authorized online reseller and Merchant of Record <strong className="text-foreground font-semibold">(MoR)</strong>. The MoR manages all transaction processing, local tax compliance, billing inquiries, and financial returns.
                    </p>
                    <p>
                      Ping After does <strong className="text-foreground font-semibold">not</strong> store, process, or transmit your raw credit card details or banking information. All payment data is handled directly by our MoR in strict compliance with PCI-DSS Level 1 security standards.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Data Protection & Encryption Architecture */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <Lock className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    3. How We Protect Your Data
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>We implement a multi-layered defense-in-depth security architecture:</p>
                    <ul className="list-disc list-outside space-y-2.5 ml-6 marker:text-primary/70">
                      <li><strong className="text-foreground font-semibold">Field-Level Encryption at Rest:</strong> Highly sensitive records, including Dead Switch Heir configurations and private notes, are encrypted at the database field level using AES-256-GCM authenticated encryption.</li>
                      <li><strong className="text-foreground font-semibold">Envelope Encryption:</strong> User Data Encryption Keys (DEKs) are individually wrapped using master Key Encryption Keys (KEKs) managed through secure environment variables and key management systems.</li>
                      <li><strong className="text-foreground font-semibold">Just-In-Time (JIT) Processing:</strong> Decrypted data exists in server RAM strictly during the momentary execution of an emergency alert dispatch and is immediately wiped from memory upon transmission.</li>
                      <li><strong className="text-foreground font-semibold">Network Isolation:</strong> Our databases and core backend services reside within an isolated Virtual Private Cloud (VPC) with zero public external access.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Data Retention & Ephemeral Trip Logic */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <Trash2 className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    4. Data Retention & Systematic Deletion
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>We adhere to strict data minimization principles. We do not store your data indefinitely:</p>
                    <ul className="list-disc list-outside space-y-2.5 ml-6 marker:text-primary/70">
                      <li><strong className="text-foreground font-semibold">Instant Location Wipe:</strong> The exact moment you confirm safety (tapping "Arrived Safely") or cancel an active trip, your precise GPS coordinates are permanently purged (Hard Deleted) from our active databases.</li>
                      <li><strong className="text-foreground font-semibold">Historical Metadata:</strong> We retain brief, anonymized metadata (without precise GPS coordinates) of your last 5 trips solely to populate your personal account history dashboard.</li>
                      <li><strong className="text-foreground font-semibold">Heir System Package:</strong> If the background Dead Switch triggers after an extended period of inactivity, the emergency data package dispatched to your Trustee contains full details of your <em className="text-foreground">most recent trip</em> along with summarized, non-precise metadata of your <em className="text-foreground">2 preceding trips</em> (totaling 3 trips of context) to assist in emergency locating efforts.</li>
                      <li><strong className="text-foreground font-semibold">Smart Emergency Links:</strong> Public web links generated during an active emergency alarm self-destruct automatically after exactly 48 hours, rendering the shared location data permanently inaccessible.</li>
                      <li><strong className="text-foreground font-semibold">Pro Media Expiry:</strong> If your Pro tier subscription lapses, any large uploaded emergency files (such as audio or video attachments) are soft-locked, then permanently deleted after 60 days of inactivity to conserve storage.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5: Third-Party Infrastructure & Sub-Processors */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <Server className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    5. Third-Party Infrastructure & Sub-Processors
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>
                      To provide reliable global service, we share limited, necessary data categories with verified enterprise sub-processors under strict contractual data protection agreements:
                    </p>
                    <ul className="list-disc list-outside space-y-2.5 ml-6 marker:text-primary/70">
                      <li><strong className="text-foreground font-semibold">Mapping & Geolocation Providers:</strong> We utilize third-party mapping APIs (such as Google Maps) within the client interface strictly to translate user-selected destination points into coordinates. These providers do not receive your continuous background location.</li>
                      <li><strong className="text-foreground font-semibold">Cloud Object Storage:</strong> Emergency media files uploaded by Pro users are stored in secure, S3-compatible cloud edge networks (such as Cloudflare R2) using presigned, time-limited access URLs.</li>
                      <li><strong className="text-foreground font-semibold">Telecommunication Gateways:</strong> When an alarm triggers, user names, destination addresses, and contact details are routed through enterprise SMS and email dispatch providers (such as Resend, Twilio, or Vonage) solely for immediate message delivery.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 6: Zero Continuous Tracking */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <Eye className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    6. Zero Continuous Tracking Policy
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>Ping After is a timer-based check-in application, not a background surveillance tool. We explicitly do <strong className="text-foreground font-semibold">not</strong>:</p>
                    <ul className="list-disc list-outside space-y-2.5 ml-6 marker:text-primary/70">
                      <li>Track, log, or query your real-time GPS location in the background while your trip is running.</li>
                      <li>Sell, rent, or trade your personal data, trip metadata, or contact lists to advertisers or data brokers.</li>
                      <li>Access your device microphone, camera, or local media gallery without explicit, feature-triggered user consent.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 7: Your Rights & 7-Day Deletion Flow */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <UserCheck className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    7. Your Privacy Rights & Account Deletion
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>Depending on your jurisdiction (including GDPR in Europe and CCPA in California), you possess fundamental rights regarding your data:</p>
                    <ul className="list-disc list-outside space-y-2.5 ml-6 marker:text-primary/70">
                      <li><strong className="text-foreground font-semibold">Right to Access & Portability:</strong> You may request an export of the personal account metadata stored on our servers.</li>
                      <li><strong className="text-foreground font-semibold">Right to Rectification:</strong> You can update or correct inaccurate profile and contact information directly through your account settings.</li>
                      <li><strong className="text-foreground font-semibold">Right to Erasure (Account Deletion):</strong> You may initiate permanent account deletion at any time from your app settings. Upon request, your account enters a <strong className="text-foreground font-semibold">7-day grace period</strong> during which all automated alarms and scheduled trips are immediately suspended. If you do not log back in during these 7 days, your account, trip history, and associated cloud storage files are permanently and irreversibly hard-deleted from our production systems.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 8: Contact Us */}
            <div className="group">
              <div className="flex items-start gap-5 mb-4">
                <div className="p-3 rounded-2xl bg-secondary/40 border border-border/40 group-hover:bg-primary/10 group-hover:border-primary/20 transition-all duration-200 shrink-0 shadow-sm">
                  <Globe className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground tracking-tight">
                    8. Contact Us
                  </h2>
                  <div className="text-muted-foreground/90 space-y-4 text-base md:text-[16.5px] leading-8 pr-2 md:pr-6">
                    <p>If you have questions, privacy inquiries, or data deletion requests regarding this Privacy Policy, please reach out to our support team:</p>
                    <div className="p-6 rounded-2xl bg-card/40 border border-border/40 mt-4 space-y-2 font-sans">
                      <div><strong className="text-foreground">Email:</strong> <a href="mailto:support@pingafter.app" className="text-primary hover:underline ml-1 font-medium">support@pingafter.app</a></div>
                      <div><strong className="text-foreground">Operator:</strong> <span className="text-muted-foreground ml-1">VANT HQ</span></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border/30">
        <div className="container mx-auto px-6 text-center text-sm text-muted-foreground font-sans">
          © {new Date().getFullYear()} VANT. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;